import { Flex } from "@redotech/arbiter/flex";
import type { RedoListItem } from "@redotech/arbiter/list/redo-list";
import { Text } from "@redotech/arbiter/text";
import { useRequiredContext } from "@redotech/react-util/context";
import { useLoad } from "@redotech/react-util/load";
import {
  assignInsightSlot,
  qualifySupportInsights,
  supportInsightDisabledReason,
  supportInsightLabels,
  supportInsightSignalsFromTeam,
  type SupportInsightMetaConnections,
} from "@redotech/redo-support-ai-common/insights/qualify-support-insights";
import {
  supportInsightLocationHasCopy,
  supportInsightMessage,
} from "@redotech/redo-support-ai-common/insights/support-insight-figures";
import {
  assignInsightLocation,
  insightLocationsUsedElsewhere,
  supportInsightLocations,
} from "@redotech/redo-support-ai-common/insights/support-insight-locations";
import {
  SUPPORT_INSIGHT_METRIC_SLOT_COUNT,
  SupportInsightActionId,
  teamsPageBannerLocationId,
  type SupportInsightMetricLocationId,
} from "@redotech/redo-support-settings-common/support-insight-slots";
import { Team } from "@redotech/redo-team-common/team";
import { SettingsChildren } from "@redotech/redo-web/settings-rows/settings-children";
import { SettingsControlRow } from "@redotech/redo-web/settings-rows/settings-control-row";
import {
  SettingsDropdownRow,
  settingsTextOption,
} from "@redotech/redo-web/settings-rows/settings-dropdown-row";
import { SettingsGroup } from "@redotech/redo-web/settings-rows/settings-group";
import { SettingsNestedRows } from "@redotech/redo-web/settings-rows/settings-nested-rows";
import { SettingsNoteRow } from "@redotech/redo-web/settings-rows/settings-note-row";
import { SettingsStaticRow } from "@redotech/redo-web/settings-rows/settings-static-row";
import { SettingsToggleRow } from "@redotech/redo-web/settings-rows/settings-toggle-row";
import { memo } from "react";
import { RedoAdminRpcClientContext } from "../../../../app/redo-admin-rpc-client-provider";
import { type SupportInsightConfiguration } from "./support-insight-configuration";

const TEAMS_PAGE_BANNER_LOCATION = "Banner on Teams page";

const locationOptions: RedoListItem<SupportInsightMetricLocationId>[] =
  supportInsightLocations.map((location) =>
    "recommended" in location
      ? {
          id: location.id,
          infoBadge: { color: "brand", text: "Recommended" },
          text: location.label,
          type: "text",
          value: location.id,
        }
      : {
          id: location.id,
          text: location.label,
          type: "text",
          value: location.id,
        },
  );

const actionOptions = SupportInsightActionId.map((id) =>
  settingsTextOption(id, supportInsightLabels[id]),
);

export const SupportInsights = memo(function SupportInsights({
  configuration,
  metaConnections,
  setConfiguration,
  team,
}: {
  configuration: SupportInsightConfiguration;
  metaConnections: SupportInsightMetaConnections;
  setConfiguration(configuration: SupportInsightConfiguration): void;
  team: Team;
}) {
  const rpcClient = useRequiredContext(RedoAdminRpcClientContext);
  const metricsLoad = useLoad(
    (signal) =>
      rpcClient.getSupportInsightMetrics({ teamId: team._id }, { signal }),
    [rpcClient, team._id],
  );
  const signals = supportInsightSignalsFromTeam(team, metaConnections);
  const qualified = qualifySupportInsights(signals);
  const slots = configuration.actions;
  const locations = configuration.locations;
  const disabledActions = SupportInsightActionId.flatMap((id) => {
    const tooltip = supportInsightDisabledReason(id, signals);
    return tooltip ? [{ value: id, tooltip }] : [];
  });

  return (
    <Flex dir="column" gap="xl" w="full">
      {qualified.length === 0 ? (
        <SettingsNoteRow>
          This merchant does not qualify for an insight. Support, the chat
          widget, and the AI channels these actions cover are already
          configured.
        </SettingsNoteRow>
      ) : null}
      {slots.map((slotId, index) => {
        const location = locations[index] ?? null;
        const teamsPageBanner = index === SUPPORT_INSIGHT_METRIC_SLOT_COUNT;
        const messageLocation = teamsPageBanner
          ? teamsPageBannerLocationId
          : location;
        const countedLocation =
          messageLocation !== null &&
          supportInsightLocationHasCopy(messageLocation);
        const volume = metricsLoad.value;
        const message =
          slotId && countedLocation && volume
            ? supportInsightMessage(messageLocation, slotId, signals, volume)
            : undefined;
        const totalsReady =
          metricsLoad.value !== undefined || metricsLoad.error !== undefined;
        const preview =
          !slotId || !countedLocation || (volume && !message)
            ? undefined
            : (message ??
              (totalsReady
                ? "Ticket totals are unavailable for this merchant."
                : "Loading this merchant's ticket totals."));
        const shown = configuration.enabled[index] !== false;
        return (
          <SettingsGroup key={index}>
            <SettingsNestedRows>
              <SettingsToggleRow
                setValue={(value) => {
                  setConfiguration({
                    ...configuration,
                    enabled: configuration.enabled.map((current, slot) =>
                      slot === index ? value : current,
                    ),
                  });
                }}
                testId={`support-insight-enabled-${index}`}
                title={`Insight ${index + 1}`}
                value={shown}
              />
              <SettingsChildren visible={shown}>
                {teamsPageBanner ? (
                  <SettingsControlRow controlWidth="content" title="Location">
                    <Text fontSize="xs" fontWeight="medium" textColor="primary">
                      {TEAMS_PAGE_BANNER_LOCATION}
                    </Text>
                  </SettingsControlRow>
                ) : (
                  <SettingsDropdownRow<SupportInsightMetricLocationId>
                    disabledOptions={insightLocationsUsedElsewhere(
                      locations,
                      index,
                    )}
                    options={locationOptions}
                    setValue={(value) => {
                      setConfiguration({
                        ...configuration,
                        locations: assignInsightLocation(
                          locations,
                          index,
                          value,
                        ),
                      });
                    }}
                    title="Location"
                    value={location}
                  />
                )}
                <SettingsDropdownRow<SupportInsightActionId>
                  disabledOptions={disabledActions}
                  options={actionOptions}
                  placeholder="Choose an insight"
                  setValue={(value) => {
                    setConfiguration({
                      ...configuration,
                      actions: assignInsightSlot(slots, index, value),
                    });
                  }}
                  title="Action"
                  value={slotId}
                />
                {preview ? (
                  <SettingsStaticRow title="Insight preview" value={preview} />
                ) : qualified.length > 0 && !slotId ? (
                  <SettingsNoteRow>
                    Pick an insight for this spot.
                  </SettingsNoteRow>
                ) : null}
              </SettingsChildren>
            </SettingsNestedRows>
          </SettingsGroup>
        );
      })}
    </Flex>
  );
});
